import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Globals } from '../shared/globals';
import { PayoutSettings } from '../models/payout.settings.model';
import { AuthService } from './auth.service';
import { JsonSerializer } from 'typescript-json-serializer';
import { Api } from '../shared/api';
import { BalanceTransactionModel } from '../models/balance.transaction.model';
import { PayoutCreateDto } from '../models/payout-create-dto.model';

@Injectable()
export class PayoutService {
  private payoutSettingsUrl: string;
  private payoutVatRate: string;
  private readonly payoutsUrl: string;
  private jsonSerializer = new JsonSerializer();
  private balanceHistoryUrl: string;
  private autoPayoutUrl: string;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private globals: Globals,
    private api: Api
  ) {
    this.payoutSettingsUrl = this.globals.apiUrl('StationOwnerPayoutSettings');
    this.payoutVatRate = this.globals.apiUrl('payout/settings/vat');
    this.payoutsUrl = this.globals.apiUrl('account/payouts');
    this.balanceHistoryUrl = this.globals.apiUrl('account/balance');
    this.autoPayoutUrl = globals.apiUrl('payout/settings/schedule');
  }

  public async getBalanceHistory(
    startDate: string,
    endDate: string
  ): Promise<BalanceTransactionModel[]> {
    const params = new URLSearchParams({
      StartDate: startDate,
      EndDate: endDate,
    });

    const response = await this.api.httpGet<BalanceTransactionModel[]>(
      `${this.balanceHistoryUrl}/history?${params}`
    );

    return this.jsonSerializer.deserializeObjectArray(
      response,
      BalanceTransactionModel
    );
  }

  public async resendPayoutReceipt(payoutId: string): Promise<void> {
    const url = `${this.payoutsUrl}/${payoutId}/receipt`;
    return this.api.httpPost(url, {});
  }

  getPayoutSettings(): Promise<PayoutSettings> {
    return this.authService
      .getToken()
      .then((result) => {
        const basicToken = 'Basic ' + result;
        return this.http
          .get(this.payoutSettingsUrl, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: basicToken,
            },
          })
          .toPromise()
          .then((response) => {
            return response;
          })
          .catch((err) => {
            return err;
          });
      })
      .catch((err) => {
        Promise.reject(err);
      });
  }

  postPayoutSettings(payoutSettings: PayoutSettings) {
    return this.authService
      .getToken()
      .then((result) => {
        const basicToken = 'Basic ' + result;

        return this.http
          .post(this.payoutSettingsUrl, payoutSettings, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: basicToken,
            },
          })
          .toPromise()
          .then((response) => {
            return response;
          })
          .catch(this.handleError);
      })
      .catch((err) => {
        return err;
      });
  }

  postPayoutTaxRate(payoutSettings: PayoutSettings) {
    return this.authService
      .getToken()
      .then((result) => {
        const basicToken = 'Basic ' + result;

        return this.http
          .post(this.payoutVatRate, payoutSettings, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: basicToken,
            },
          })
          .toPromise()
          .then((response) => {
            return response;
          })
          .catch(this.handleError);
      })
      .catch((err) => {
        return err;
      });
  }

  async createPayout(comment: string): Promise<void> {
    const payoutCreateDto: PayoutCreateDto = {
      comment,
    };

    return await this.api.httpPost(this.payoutsUrl, payoutCreateDto);
  }

  async updateAutoPayoutInfo(data: {
    interval: string;
    nextPayoutAt: string;
    isEnabled: boolean;
  }) {
    try {
      return await this.api.httpPatch(this.autoPayoutUrl, {
        Interval: data.interval,
        NextPayoutAt: data.nextPayoutAt,
        IsEnabled: data.isEnabled,
      });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  private handleError(error: any): Promise<any> {
    return Promise.reject(error.message || error);
  }
}
